import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { BuyerOffer, BuyerOfferStatus } from '@/types';
import { DateTimeDisplay } from '@/components/localization';
import { BuyerOfferStatusIndicator } from '@/components/StatusIndicator';
import { ExpandableCard, CardSection, ExpandableCardHandle, SimpleCard } from '@/components/Cards';
import { RejectedStatusReason } from '@/components/StatusReason';
import BuyerOfferLineGroupDisplay from './BuyerOfferLineGroupDisplay';
import ProfileAndUserDisplay from '@/components/ProfileAndUserDisplay';
import { PriceDisplay } from '../fees';

interface BuyerOfferDisplayProps {
    buyerOffer: BuyerOffer;
}

const BuyerOfferDisplay: React.FC<BuyerOfferDisplayProps> = ({ buyerOffer }) => {
    const [expanded, setExpanded] = useState(false);

    const renderDetails = () => {
        return (
            <>
                <SimpleCard>
                    <div>
                        <ProfileAndUserDisplay
                            profileId={buyerOffer.sentByProfileId}
                            profileName={buyerOffer.sentByProfileName}
                            userId={buyerOffer.sentByUserId}
                            userName={buyerOffer.sentByUserName}
                        />

                        {buyerOffer.sendNote && (
                            <div>
                                <h6>
                                    <Trans>Offer message</Trans>
                                </h6>
                                {buyerOffer.sendNote}
                            </div>
                        )}
                    </div>
                </SimpleCard>

                {buyerOffer.lineGroups.map((x) => (
                    <BuyerOfferLineGroupDisplay key={x.id} lineGroup={x} />
                ))}
            </>
        );
    };

    return (
        <ExpandableCard expanded={expanded} expandableContent={renderDetails()} type="outside">
            <CardSection caption={<Trans>Offer ID</Trans>} width="120px">
                #{buyerOffer.id}
            </CardSection>

            <CardSection caption={<Trans># of quotes</Trans>} width="80px">
                {buyerOffer.lineGroups.length}
            </CardSection>

            <CardSection caption={<Trans>Sent</Trans>}>
                <Trans>
                    <DateTimeDisplay value={buyerOffer.sendDate} /> by {buyerOffer.sentByUserName}
                </Trans>
            </CardSection>

            <CardSection caption={<Trans>Status</Trans>} width="120px" right>
                <BuyerOfferStatusIndicator value={buyerOffer.status} />

                {buyerOffer.status === BuyerOfferStatus.Rejected && (
                    <RejectedStatusReason reason={buyerOffer.rejectReason} userName={buyerOffer.reviewedByUserName} />
                )}

                {buyerOffer.status === BuyerOfferStatus.Retracted && (
                    <RejectedStatusReason reason={buyerOffer.retractNote} userName={buyerOffer.reviewedByUserName} />
                )}
            </CardSection>

            <CardSection caption={<Trans>Total</Trans>} className="text-right" right>
                <PriceDisplay
                    value={{ basePrice: buyerOffer.totalBasePrice, finalPrice: buyerOffer.totalFinalPrice }}
                    redacted={buyerOffer.hasRedactedPrices}
                    hideInfoIcon
                />
            </CardSection>

            <ExpandableCardHandle onExpandToggle={setExpanded} expanded={expanded} />
        </ExpandableCard>
    );
};

export default BuyerOfferDisplay;
